<template>
    <popup-modal
        custom-classes="w-768"
        position="center"
        :title="$t('interims.matrix_types.title')"
        @close="$emit('close')"
    >
        <loading v-if="fetchingTypes"></loading>
        <div v-else>

            <draggable-transition-group
                click-edit
                label="name"
                :loading="fetchingTypes"
                :options="types"
                @update="updateType"
                @deleting="startDeletingType($event)"
                @updateOrder="updateOrder"
                @updateOptions="resort($event)"
            />

            <div class="flex mt-16">
                <simple-input
                    class="flex-grow"
                    :placeholder="$t('interims.matrix_types.add_placeholder')"
                    v-model="newType"
                    @keydown.enter="addType"
                >
                    <template #after>
                        <button
                            class="flex items-center gap-8"
                            :class="creatingType || newType === '' ? 'cursor-not-allowed text-primary-40' : 'cursor-pointer'"
                            :disabled="creatingType || newType === ''"
                            @click="addType"
                        >
                            <svg-icon type="add-circle" />
                            {{ $t('interims.matrix_types.add') }}
                        </button>
                    </template>
                </simple-input>
            </div>
        </div>

        <popup-modal-confirm
            show-exclamation
            :working="performingRequest"
            v-if="deletingType" :message="$t('interims.matrix_types.delete_message')" :title="$t('interims.matrix_types.delete_title')"
            @close="stopDeletingType"
            @confirm="deleteType"
        />
    </popup-modal>
</template>
<script>
import PopupModal from './ui/PopupModal';
import * as types from '../services/InterimMatrixTypeService';
import InterimMatrixType from '../models/InterimMatrixType';
import {normalizeJson} from '../../shared/helpers';
import Loading from './ui/Loading';
import SimpleInput from './forms/Input';
import SimpleButton from './ui/SimpleButton';
import SvgIcon from './ui/SvgIcon';
import draggable from 'vuedraggable';
import {mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useNotificationsStore} from '../state/modules/notifications';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import PopupModalConfirm from './ui/PopupModalConfirm';
import DraggableTransitionGroup from "./ui/DraggableTransitionGroup.vue";

export default {
    components: {
        DraggableTransitionGroup,
        PopupModalConfirm, SimpleButton, SimpleInput, Loading, PopupModal, SvgIcon, draggable},
    data() {
        return {
            fetchingTypes: false,

            newType: '',
            creatingType: false,

            drag: false,
            currentType: null,

            editingType: null,
            deletingType: null,
            performingRequest: false,
        };
    },
    props: {
        activeItem: {
            required: false,
            default: 'interim',
        },
    },
    computed: {
        types: {
            get() {
                return useRepo(InterimMatrixType).where('type', this.activeItem).orderBy('order').get();
            },
            set(types) {
                this.resort(types);
            },
        },
        dragOptions() {
            return {
                animation: 200,
                group: 'items',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        fetch() {
            this.fetchingTypes = true;

            types.index()
                .then(result => {
                    return useRepo(InterimMatrixType).save(normalizeJson(result.data));
                })
                .then(() => {
                    this.fetchingTypes = false;
                });
        },

        addType() {
            if (this.creatingType) {
                return;
            }

            this.creatingType = true;

            types.store({
                data: {
                    attributes: {
                        name: this.newType,
                        type: this.activeItem,
                    },
                },
            }).then(result => useRepo(InterimMatrixType).save(normalizeJson(result)))
                .then(() => {
                    this.newType = '';
                    this.creatingType = false;
                });
        },

        resort(sortedTypes) {
            let sort = sortedTypes.map((type, index) => ({id: type.id, order: index}));

            useRepo(InterimMatrixType).whereIn('id', sort.map(sort => sort.id)).get()
                .map(type => ({
                    id: type.id,
                    order: type.order,
                }));

            useRepo(InterimMatrixType).save(sort);
        },
        updateOrder({evt, item, done}) {
            types.update(
                item.id,
                {data: {attributes: {order: evt.newDraggableIndex}}},
            ).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: 'type'}),
                });
            }).then(() => done());

            this.currentType = null;
            this.drag = false;
        },

        updateType(event) {
            const {item, value} = event;
            this.editingType = {
                id: item.id,
                name: value,
            }
            this.saveType();
        },
        startDeletingType(type) {
            this.deletingType = type;
        },
        stopDeletingType() {
            this.deletingType = null;
        },

        saveType() {
            this.performingRequest = true;

            types.update(this.editingType.id, {data: {attributes: {name: this.editingType.name}}})
                .then((result) => useRepo(InterimMatrixType).save(normalizeJson(result)))
                .then(() => {
                    this.editingType = null;
                    this.performingRequest = false;
                });
        },
        deleteType() {
            this.performingRequest = true;
            types.destroy(this.deletingType.id)
                .then(() => useRepo(InterimMatrixType).destroy(this.deletingType.id))
                .then(() => {
                    this.deletingType = null;
                    this.performingRequest = false;
                });
        },
    },
    mounted() {
        this.fetch();
    },
};
</script>
